import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { X } from 'lucide-react'
import {
  createFaqEntry,
  createFaqGroup,
  deleteFaqEntry,
  faqErrorCode,
  faqErrorDetail,
  faqErrorMessage,
  getFaqEntry,
  updateFaqEntry,
} from '../../api/faq'
import FaqAttachments from './FaqAttachments'
import { DEFAULT_LIMITS, addAlt, changedFields, contentFromForm, fieldOfError, formFromEntry, isDirty } from './faqForm'

const NEW_GROUP = '__new__'
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
const field = 'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 disabled:bg-slate-50'
const label = 'flex items-center justify-between text-sm font-semibold text-slate-700'

function Counter({ value, max }) {
  return <span className={`text-xs font-normal ${value > max ? 'text-red-600' : 'text-slate-400'}`}>{value}/{max}</span>
}

// Create, edit or (read-only) view one FAQ entry. Beside the list on wide
// screens; a full-screen dialog that keeps the focus inside on narrow ones.
export default function FaqEditPanel({
  tenantId, entry, prefill, groups, limits, botUsesFaq, readOnly = false, isDesktop = true,
  onClose, onSaved, onDeleted, onReloaded, onOpenExisting, onGroupsChanged, onPermissionDenied, onDirtyChange,
}) {
  const { t } = useTranslation()
  const caps = { ...DEFAULT_LIMITS, ...limits }
  // `prefill` ({question, answer}) starts a new entry from text found elsewhere (a legacy
  // document's pair refused as too long): shown in full, counters red until shortened.
  const [form, setForm] = useState(() => (
    prefill ? { ...formFromEntry(null), question: prefill.question ?? '', answer: prefill.answer ?? '' } : formFromEntry(entry)
  ))
  const [altDraft, setAltDraft] = useState('')
  const [groupDraft, setGroupDraft] = useState(null) // text of a group being created inline
  const [problem, setProblem] = useState(null) // { field, code, message, existingId }
  const [busy, setBusy] = useState(false)
  const root = useRef(null)
  const dirty = !readOnly && (isDirty(form, entry) || Boolean(altDraft.trim()) || Boolean(groupDraft?.trim()))
  const canAddGroup = groups.length < (caps.groups ?? 20)

  const set = (name, value) => {
    setForm((previous) => ({ ...previous, [name]: value }))
    // A conflict stays until the entry is reloaded: its "reload" link must not vanish on a keystroke.
    setProblem((previous) => (previous?.code === 'edit_conflict' ? previous : null))
  }

  const close = () => {
    if (busy) return // a save or delete is running: its result must not be lost
    if (dirty && !window.confirm(t('faq.panel.discardConfirm'))) return
    onClose()
  }

  // The page asks before replacing a panel that has unsaved changes.
  useEffect(() => {
    onDirtyChange?.(dirty)
    return () => onDirtyChange?.(false)
  }, [dirty, onDirtyChange])

  useEffect(() => {
    const first = root.current?.querySelector('input:not([disabled]), textarea:not([disabled])')
    ;(first ?? root.current?.querySelector(FOCUSABLE))?.focus()
  }, [])

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      close()
      return
    }
    if (event.key !== 'Tab' || isDesktop) return
    // Narrow screens: the panel is a modal dialog, so Tab stays inside it.
    const items = [...root.current.querySelectorAll(FOCUSABLE)]
    if (items.length === 0) return
    const first = items[0]
    const last = items.at(-1)
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const fail = (error) => {
    const code = faqErrorCode(error)
    const detail = faqErrorDetail(error)
    if (code === 'permission_denied') onPermissionDenied?.()
    setProblem({ field: fieldOfError(detail), code, message: faqErrorMessage(t, error), existingId: detail.existing_id })
  }

  const commitAltDraft = () => {
    const next = addAlt(form.alts, altDraft, caps.alts)
    setAltDraft('')
    if (next !== form.alts) set('alts', next)
    return next
  }

  const save = async (event) => {
    event.preventDefault()
    if (busy || readOnly) return
    const ready = { ...form, alts: altDraft.trim() ? addAlt(form.alts, altDraft, caps.alts) : form.alts }
    setBusy(true)
    setProblem(null)
    try {
      let saved
      if (entry) {
        const changes = changedFields(ready, entry)
        if (Object.keys(changes).length === 0) {
          onClose()
          return
        }
        saved = await updateFaqEntry(tenantId, entry.id, { ...changes, expected_updated_at: entry.updated_at })
      } else {
        saved = await createFaqEntry(tenantId, contentFromForm(ready))
      }
      if (saved.warnings?.includes('placeholder_text')) toast.warn(t('faq.panel.placeholderWarning'))
      onSaved(saved, { created: !entry })
    } catch (error) {
      fail(error)
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!window.confirm(t('faq.panel.deleteConfirm', { question: entry.question }))) return
    setBusy(true)
    try {
      await deleteFaqEntry(tenantId, entry.id)
      onDeleted(entry)
    } catch (error) {
      fail(error)
    } finally {
      setBusy(false)
    }
  }

  const reload = async () => {
    setBusy(true)
    try {
      onReloaded(await getFaqEntry(tenantId, entry.id))
    } catch (error) {
      fail(error)
    } finally {
      setBusy(false)
    }
  }

  const createGroup = async () => {
    const name = (groupDraft || '').trim()
    if (!name) return
    setBusy(true)
    try {
      const group = await createFaqGroup(tenantId, name)
      onGroupsChanged?.()
      setGroupDraft(null)
      set('group_id', group.id)
    } catch (error) {
      if (faqErrorCode(error) === 'permission_denied') onPermissionDenied?.()
      setProblem({ field: 'group', code: faqErrorCode(error), message: faqErrorMessage(t, error) })
    } finally {
      setBusy(false)
    }
  }

  const errorFor = (name) => (problem?.field === name ? (
    <p role="status" aria-live="polite" className="text-xs text-red-600">
      <span>{problem.message}</span>
      {problem.code === 'duplicate_question' && problem.existingId && (
        <button type="button" onClick={() => onOpenExisting(problem.existingId)} className="ml-2 font-semibold underline">
          {t('faq.panel.openExisting')}
        </button>
      )}
      {problem.code === 'edit_conflict' && (
        <button type="button" onClick={reload} className="ml-2 font-semibold underline">
          {t('faq.panel.reload')}
        </button>
      )}
    </p>
  ) : null)

  const title = readOnly ? t('faq.panel.viewTitle') : t(entry ? 'faq.panel.editTitle' : 'faq.panel.createTitle')
  const shell = isDesktop
    ? 'w-full max-w-[400px] shrink-0 rounded-lg border border-slate-200 bg-white'
    : 'fixed inset-0 z-50 overflow-y-auto bg-white'

  return (
    <aside
      ref={root}
      className={shell}
      onKeyDown={onKeyDown}
      aria-label={title}
      {...(isDesktop ? {} : { role: 'dialog', 'aria-modal': 'true' })}
    >
      <form onSubmit={save} className="flex flex-col gap-4 p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          <button type="button" onClick={close} aria-label={t('faq.panel.close')} title={t('faq.panel.close')}>
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        {entry?.source === 'quick_answer' && (
          <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            {t('faq.panel.fromQuickAnswer')}{' '}
            <Link to="/quick-answers" className="font-medium text-[#1535c8] underline">{t('faq.panel.openQuickAnswer')}</Link>
          </p>
        )}

        <div className="flex flex-col gap-1">
          <label htmlFor="faq-question" className={label}>
            {t('faq.panel.question')}<Counter value={form.question.length} max={caps.question} />
          </label>
          <input
            id="faq-question" type="text" value={form.question} disabled={readOnly}
            onChange={(event) => set('question', event.target.value)} className={field}
          />
          {errorFor('question')}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="faq-answer" className={label}>
            {t('faq.panel.answer')}<Counter value={form.answer.length} max={caps.answer} />
          </label>
          <textarea
            id="faq-answer" rows={5} value={form.answer} disabled={readOnly}
            onChange={(event) => set('answer', event.target.value)} className={field}
          />
          <p className="text-xs text-slate-400">{t('faq.panel.catalogHelp')}</p>
          {errorFor('answer')}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="faq-alt" className={label}>
            {t('faq.panel.alts')}<Counter value={form.alts.length} max={caps.alts} />
          </label>
          <div className="flex flex-wrap gap-1.5">
            {form.alts.map((alt) => (
              <span key={alt} className="inline-flex max-w-full items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700">
                <span className="truncate">{alt}</span>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => set('alts', form.alts.filter((other) => other !== alt))}
                    aria-label={t('faq.panel.removeAlt', { alt })}
                  >
                    <X size={12} />
                  </button>
                )}
              </span>
            ))}
          </div>
          {!readOnly && form.alts.length < caps.alts && (
            <div className="flex gap-2">
              <input
                id="faq-alt" type="text" value={altDraft} maxLength={caps.alt}
                onChange={(event) => setAltDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ',') {
                    event.preventDefault()
                    commitAltDraft()
                  }
                }}
                className={field}
              />
              <button type="button" onClick={commitAltDraft} className="shrink-0 text-sm font-semibold text-[#1535c8]">
                {t('faq.panel.addAlt')}
              </button>
            </div>
          )}
          {errorFor('alts')}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="faq-group" className={label}>{t('faq.panel.group')}</label>
          {groupDraft === null ? (
            <select
              id="faq-group" value={form.group_id} disabled={readOnly} className={field}
              onChange={(event) => (event.target.value === NEW_GROUP ? setGroupDraft('') : set('group_id', event.target.value))}
            >
              <option value="">{t('faq.ungrouped')}</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
              {!readOnly && canAddGroup && <option value={NEW_GROUP}>{t('faq.panel.newGroup')}</option>}
            </select>
          ) : (
            <div className="flex gap-2">
              <input
                id="faq-group" type="text" value={groupDraft} maxLength={40} autoFocus
                aria-label={t('faq.panel.newGroupName')} placeholder={t('faq.panel.newGroupName')}
                onChange={(event) => setGroupDraft(event.target.value)} className={field}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault() // creates the group; it must not submit the entry
                    createGroup()
                  } else if (event.key === 'Escape') {
                    event.stopPropagation() // leaves the group input, not the panel
                    setGroupDraft(null)
                  }
                }}
              />
              <button type="button" onClick={createGroup} disabled={busy || !groupDraft.trim()} className="shrink-0 text-sm font-semibold text-[#1535c8] disabled:opacity-50">
                {t('faq.panel.createGroup')}
              </button>
              <button type="button" onClick={() => setGroupDraft(null)} className="shrink-0 text-sm text-slate-500">
                {t('faq.panel.cancel')}
              </button>
            </div>
          )}
          {errorFor('group')}
        </div>

        <div className="flex flex-col gap-1">
          <span className={label}>{t('faq.panel.attachments')}</span>
          <FaqAttachments
            links={form.links} onChange={(links) => set('links', links)}
            max={caps.attachments} labelMax={caps.label} readOnly={readOnly}
          />
          {errorFor('links')}
        </div>

        {errorFor('form')}

        {!readOnly && (
          <>
            <p className="text-xs text-slate-400">{t(botUsesFaq ? 'faq.panel.footer.bot' : 'faq.panel.footer.neutral')}</p>
            <div className="flex items-center justify-between gap-2">
              {entry ? (
                <button type="button" onClick={remove} disabled={busy} className="text-sm font-semibold text-red-600 disabled:opacity-50">
                  {t('faq.panel.delete')}
                </button>
              ) : <span />}
              <div className="flex gap-2">
                <button type="button" onClick={close} className="h-10 rounded-lg border border-slate-200 px-4 text-sm font-medium text-slate-700">
                  {t('faq.panel.cancel')}
                </button>
                <button type="submit" disabled={busy} className="h-10 rounded-lg bg-[#1535c8] px-4 text-sm font-semibold text-white disabled:opacity-60">
                  {t('faq.panel.save')}
                </button>
              </div>
            </div>
          </>
        )}
      </form>
    </aside>
  )
}
